import { FormLabel, Grid } from '@mui/material';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Control } from 'react-hook-form';
import { FormControl } from '../../../ui/components/pure/form-control/form-control.component';
import { TextField } from '../../../ui/components/pure/text-field/text-field.component';
import { useMobilePhoneFormatter } from '../../../ui/formatters/mobile-phone.formatter';
import { ACCESS_CODE_MAX_LENGTH } from '../../../ui/util/access-code.validator';
import { useTextStyles } from '../../../ui/theme/text';
import { FormConfigBase } from '../../../core/forms/form-config';
import { CourierFormState, LoginTab } from '../logic/courier-form.map';
import { fullWidthStyle, loginDetailsSectionStyle, rowGroupStyle, useLoginTabStyles } from './courier-form.styles';

/**
 * The courier fields common to creating and editing one, so the two modals stay identical in
 * layout and validation and only differ in what wraps them — create adds the carrier pickers above
 * and a notes field below, and each supplies its own title and submit.
 *
 * `control` is the caller's react-hook-form control, which is why this takes no form state of its
 * own: whichever modal owns the form owns the values. The login fields are hidden with
 * `display: none` rather than unmounted, so switching tabs does not discard what was typed.
 */
export function CourierFormFields<T extends CourierFormState>({
  control: formControl,
  projectID,
  formConfig,
  activeLoginTab,
  setActiveLoginTab
}: {
  // Generic so the create form, which extends CourierFormState, can pass its own typed control.
  control: Control<T>;
  projectID?: string;
  formConfig: FormConfigBase<CourierFormState>;
  activeLoginTab: LoginTab;
  setActiveLoginTab(tab: LoginTab): void;
}) {
  // TypeScript can't resolve `Path<T>` for a generic T, so read the fields through the base form
  // type. Safe: T extends CourierFormState, so every field used here exists on the caller's form.
  const control = formControl as unknown as Control<CourierFormState>;
  const loginTabClasses = useLoginTabStyles();
  const textClasses = useTextStyles();
  const mobilePhoneFormatter = useMobilePhoneFormatter(projectID);

  return (
    <>
      <Grid container spacing={2} style={rowGroupStyle}>
        <Grid size={6}>
          <FormControl
            key={formConfig.firstName.name}
            control={control}
            name={formConfig.firstName.name}
            required={formConfig.firstName.required}
            rules={{ validate: formConfig.firstName.validate }}
            Component={TextField}
            ComponentProps={{
              textFieldVariant: 'floating' as const,
              placeholder: formConfig.firstName.label,
              fullWidth: true,
              style: fullWidthStyle
            }}
          />
        </Grid>
        <Grid size={6}>
          <FormControl
            key={formConfig.lastName.name}
            control={control}
            name={formConfig.lastName.name}
            required={formConfig.lastName.required}
            Component={TextField}
            ComponentProps={{
              textFieldVariant: 'floating' as const,
              placeholder: formConfig.lastName.label,
              fullWidth: true,
              style: fullWidthStyle
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} style={loginDetailsSectionStyle}>
        <Grid size={12}>
          <FormLabel component="legend" className={textClasses.h3}>
            Login details
          </FormLabel>
        </Grid>
        <Grid size={12}>
          <ToggleButtonGroup
            value={activeLoginTab}
            exclusive
            onChange={(_e, value: LoginTab | null) => value && setActiveLoginTab(value)}
          >
            <ToggleButton value="mobile" classes={{ selected: loginTabClasses.selected }}>
              Mobile number
            </ToggleButton>
            <ToggleButton value="externalID" classes={{ selected: loginTabClasses.selected }}>
              ID/username
            </ToggleButton>
            <ToggleButton value="accessCode" classes={{ selected: loginTabClasses.selected }}>
              ID/username &amp; access code
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        <Grid style={{ display: activeLoginTab === 'mobile' ? 'block' : 'none' }} size={12}>
          <FormControl
            key={formConfig.mobile.name}
            control={control}
            name={formConfig.mobile.name}
            required={formConfig.mobile.required}
            rules={{ validate: formConfig.mobile.validate }}
            setValueAs={(mobile) => mobilePhoneFormatter(mobile)}
            Component={TextField}
            ComponentProps={{
              type: 'tel',
              textFieldVariant: 'floating' as const,
              placeholder: formConfig.mobile.label,
              fullWidth: true,
              style: fullWidthStyle
            }}
          />
        </Grid>

        <Grid style={{ display: activeLoginTab === 'externalID' ? 'block' : 'none' }} size={12}>
          <FormControl
            key={formConfig.externalID.name}
            control={control}
            name={formConfig.externalID.name}
            required={formConfig.externalID.required}
            Component={TextField}
            ComponentProps={{
              textFieldVariant: 'floating' as const,
              placeholder: formConfig.externalID.label,
              fullWidth: true,
              style: fullWidthStyle
            }}
          />
        </Grid>

        <Grid style={{ display: activeLoginTab === 'accessCode' ? 'block' : 'none' }} size={6}>
          <FormControl
            key={formConfig.username.name}
            control={control}
            name={formConfig.username.name}
            required={formConfig.username.required}
            Component={TextField}
            ComponentProps={{
              textFieldVariant: 'floating' as const,
              placeholder: formConfig.username.label,
              fullWidth: true,
              style: fullWidthStyle
            }}
          />
        </Grid>
        <Grid style={{ display: activeLoginTab === 'accessCode' ? 'block' : 'none' }} size={6}>
          <FormControl
            key={formConfig.accessCode.name}
            control={control}
            name={formConfig.accessCode.name}
            required={formConfig.accessCode.required}
            rules={{ validate: formConfig.accessCode.validate }}
            // Digits only, and kept as a string all the way through: a leading zero is part of the
            // code, so `type: 'number'` or a parse on the way out would quietly issue a different one.
            setValueAs={(accessCode: string) => (accessCode ?? '').replace(/\D/g, '')}
            Component={TextField}
            ComponentProps={{
              // Shown rather than masked: relaying the code to the courier is the point of it, and
              // any admin who can read the courier can read the code anyway.
              type: 'text',
              inputMode: 'numeric' as const,
              slotProps: { htmlInput: { maxLength: ACCESS_CODE_MAX_LENGTH, pattern: '[0-9]*' } },
              textFieldVariant: 'floating' as const,
              placeholder: formConfig.accessCode.label,
              fullWidth: true,
              style: fullWidthStyle
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}
